// src/pages/actividades/ImportarActividadesPage.tsx
import { useState, useEffect, useMemo, useCallback, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  Upload,
  ChevronDown,
  ChevronRight,
  ArrowLeft,
  ArrowRight,
  FileSpreadsheet,
  FileText,
} from 'lucide-react';
import {
  Button,
  Textarea,
  Badge,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Card,
  CardContent,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Checkbox,
  Label,
  Separator,
  Spinner,
  SimpleTooltip,
  Input,
} from '@/components/ui';
import { cn } from '@/lib/utils';
import { parseActividadesBulk, parseActividad, type ParsedActividad } from '@/lib/semanticParser';
import { smartParse, mergeSmartIntoSemantic } from '@/lib/smartParser';
import { useCatalogIndex, type CatalogIndex } from '@/hooks/useCatalogIndex';
import { parseHierarchy, hasHierarchy, type HierarchyGroup } from '@/lib/hierarchyParser';
import {
  actividadesService,
  type BulkImportRequest,
  type BulkImportResponse,
  type BulkImportItem,
  type BulkImportChildItem,
  type BulkItemResult,
  type BulkImportDefaults,
} from '@/services/actividades';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Defaults {
  area: string;
  prioridad: string;
  cliente_id: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PLACEHOLDER_TEXT = `MARTES:
- avimarca revisar facturación 08:00
- actualiza xls timbres
PROG:
- alpesinc rutina importaciones
- Ferremarca resuelve módulo ventas
MIERCOLES:
- 16:00 junta qps
- ir a clase de yoga

Preparar presentacion Lic Jannet
    - KPIs del modulo
    - Funcionalidades basicas`;

const CODE_REFERENCE: { code: string; description: string; example: string }[] = [
  { code: 'CLI:nombre', description: 'Cliente', example: 'CLI:Neria' },
  { code: '@nombre', description: 'Contacto', example: '@Antonio' },
  { code: 'LLAMAR:', description: 'Tipo: Llamada', example: 'LLAMAR: Seguimiento' },
  { code: 'PROG:', description: 'Tipo: Programacion', example: 'PROG: Revisar modulo' },
  { code: 'IR:', description: 'Tipo: Visita', example: 'IR: Instalacion' },
  { code: 'REVISAR:', description: 'Tipo: Revision', example: 'REVISAR: Reporte' },
  { code: 'REUNIR:', description: 'Tipo: Reunion', example: 'REUNIR: Junta semanal' },
  { code: 'INSTALAR:', description: 'Tipo: Instalacion', example: 'INSTALAR: Servidor' },
  { code: 'DOCUMENTAR:', description: 'Tipo: Documentacion', example: 'DOCUMENTAR: API' },
  { code: 'CAPACITAR:', description: 'Tipo: Capacitacion', example: 'CAPACITAR: Equipo' },
  { code: 'INVESTIGAR:', description: 'Tipo: Investigacion', example: 'INVESTIGAR: Bug' },
  { code: 'FEC:expr', description: 'Fecha agendada', example: 'FEC:lunes, FEC:15Mar' },
  { code: 'LIM:expr', description: 'Fecha limite', example: 'LIM:viernes, LIM:30Mar' },
  { code: '+nombre', description: 'Proyecto', example: '+QOps' },
  { code: 'EN:lugar', description: 'Ubicacion', example: 'EN:oficina' },
  { code: '#tag', description: 'Etiqueta', example: '#bugfix #urgente' },
  { code: '!', description: 'Prioridad AA', example: 'Tarea importante !' },
  { code: '!!', description: 'Prioridad AAA', example: 'Tarea critica !!' },
  { code: 'AREA:T/A/P', description: 'Area', example: 'AREA:T (Trabajo)' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDateISO(d: Date): string {
  return d.toISOString().split('T')[0];
}

/**
 * Apply semantic parser + smart fuzzy matching to a single line.
 * If catalog is available, detects clients, contacts, times, areas from natural text.
 */
function smartParseActividad(
  line: string,
  catalog: CatalogIndex | null,
  referenceDate?: Date,
): ParsedActividad {
  const semantic = parseActividad(line, referenceDate);
  if (!catalog || catalog.isLoading) return semantic;

  const smart = smartParse(semantic.titulo, {
    clientes: catalog.clientes,
    contactos: catalog.contactos,
  }, referenceDate);
  return mergeSmartIntoSemantic(semantic, smart);
}

function parsedToItemFields(p: ParsedActividad) {
  return {
    texto_original: p.textoOriginal,
    titulo: p.titulo,
    cliente_ref: p.clienteRef,
    contacto_ref: p.contactoRef,
    tipo_actividad_codigo: p.tipoActividadCodigo,
    fecha_agendada: p.fechaAgendada ? formatDateISO(p.fechaAgendada) : undefined,
    hora_agendada: p.horaAgendada,
    fecha_limite: p.fechaLimite ? formatDateISO(p.fechaLimite) : undefined,
    hora_limite: p.horaLimite,
    proyecto_ref: p.proyectoRef,
    ubicacion_ref: p.ubicacionRef,
    etiqueta_refs: p.etiquetaRefs.length > 0 ? p.etiquetaRefs : undefined,
    prioridad: p.prioridad,
    area: p.area,
  };
}

/**
 * Parse flat text into BulkImportItems with smart matching + day header support.
 * Day headers (MARTES:, MIERCOLES, etc.) propagate fecha_agendada to subsequent lines.
 */
function convertTextToItems(
  text: string,
  catalog: CatalogIndex | null,
): BulkImportItem[] {
  const lines = text.split('\n').filter((l) => l.trim().length > 0);
  const items: BulkImportItem[] = [];
  let currentDayDate: Date | undefined;
  let lineNum = 0;

  for (const line of lines) {
    // Check for day header
    const smart = catalog && !catalog.isLoading
      ? smartParse(line.trim(), { clientes: catalog.clientes, contactos: catalog.contactos })
      : null;

    if (smart?.isDayHeader && smart.fechaAgendada) {
      currentDayDate = smart.fechaAgendada;
      continue; // Don't create an activity for the header
    }

    lineNum++;
    const parsed = smartParseActividad(line.trim(), catalog);

    // Inherit day date if not already set
    if (!parsed.fechaAgendada && currentDayDate) {
      parsed.fechaAgendada = currentDayDate;
    }

    items.push({
      linea: lineNum,
      ...parsedToItemFields(parsed),
    });
  }

  return items;
}

/**
 * Convert hierarchy groups into BulkImportItems with smart matching.
 * Day headers within hierarchy groups are supported.
 */
function convertHierarchyToItems(
  groups: HierarchyGroup[],
  catalog: CatalogIndex | null,
  referenceDate?: Date,
): BulkImportItem[] {
  const items: BulkImportItem[] = [];
  let currentDayDate: Date | undefined;
  let groupNum = 0;

  for (const group of groups) {
    // Check if parent line is a day header
    const smart = catalog && !catalog.isLoading
      ? smartParse(group.line.trim(), { clientes: catalog.clientes, contactos: catalog.contactos }, referenceDate)
      : null;

    if (smart?.isDayHeader && smart.fechaAgendada) {
      currentDayDate = smart.fechaAgendada;
      // Day header with children: promote children to top-level items with this date
      if (group.children.length > 0) {
        for (const childLine of group.children) {
          groupNum++;
          const childParsed = smartParseActividad(childLine, catalog, referenceDate);
          if (!childParsed.fechaAgendada) {
            childParsed.fechaAgendada = currentDayDate;
          }
          items.push({
            linea: groupNum,
            ...parsedToItemFields(childParsed),
          });
        }
      }
      continue;
    }

    groupNum++;
    const parentParsed = smartParseActividad(group.line, catalog, referenceDate);

    // Inherit day date
    if (!parentParsed.fechaAgendada && currentDayDate) {
      parentParsed.fechaAgendada = currentDayDate;
    }

    const children: BulkImportChildItem[] = group.children.map((childLine, ci) => {
      const childParsed = smartParseActividad(childLine, catalog, referenceDate);
      if (!childParsed.fechaAgendada && currentDayDate) {
        childParsed.fechaAgendada = currentDayDate;
      }
      return {
        linea: groupNum * 10000 + ci + 1,
        ...parsedToItemFields(childParsed),
      };
    });

    items.push({
      linea: groupNum,
      ...parsedToItemFields(parentParsed),
      children: children.length > 0 ? children : undefined,
    });
  }

  return items;
}

function buildDefaults(d: Defaults): BulkImportDefaults | undefined {
  const result: BulkImportDefaults = {};
  if (d.area) result.area = d.area;
  if (d.prioridad) result.prioridad = d.prioridad;
  if (d.cliente_id) result.cliente_id = d.cliente_id;
  return Object.keys(result).length > 0 ? result : undefined;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StepIndicator({ step }: { step: 1 | 2 | 3 }) {
  const steps = [
    { num: 1, label: 'Datos' },
    { num: 2, label: 'Vista previa' },
    { num: 3, label: 'Resultado' },
  ];

  return (
    <div className="flex items-center gap-2">
      {steps.map((s, i) => (
        <div key={s.num} className="flex items-center gap-2">
          <div
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold',
              step === s.num
                ? 'bg-primary text-primary-foreground'
                : step > s.num
                  ? 'bg-green-100 text-green-800'
                  : 'bg-muted text-muted-foreground'
            )}
          >
            {step > s.num ? <CheckCircle className="h-4 w-4" /> : s.num}
          </div>
          <span
            className={cn(
              'text-sm font-medium',
              step === s.num ? 'text-foreground' : 'text-muted-foreground'
            )}
          >
            {s.label}
          </span>
          {i < steps.length - 1 && (
            <Separator className="w-8" orientation="horizontal" />
          )}
        </div>
      ))}
    </div>
  );
}

function LivePreview({ text }: { text: string }) {
  const stats = useMemo(() => {
    if (!text.trim()) return null;
    const isHierarchical = hasHierarchy(text);
    if (isHierarchical) {
      const groups = parseHierarchy(text);
      const padres = groups.length;
      const subtareas = groups.reduce((sum, g) => sum + g.children.length, 0);
      const total = padres + subtareas;
      // Parse all lines for semantic stats
      const allLines = groups.flatMap((g) => [g.line, ...g.children]);
      const parsed = allLines.map((l) => parseActividad(l));
      const conCliente = parsed.filter((p) => p.clienteRef).length;
      const conFecha = parsed.filter((p) => p.fechaAgendada).length;
      const conPrioridadAlta = parsed.filter(
        (p) => p.prioridad === 'AA' || p.prioridad === 'AAA'
      ).length;
      return { total, padres, subtareas, conCliente, conFecha, conPrioridadAlta, isHierarchical };
    }
    const parsed = parseActividadesBulk(text);
    const total = parsed.length;
    const conCliente = parsed.filter((p) => p.clienteRef).length;
    const conFecha = parsed.filter((p) => p.fechaAgendada).length;
    const conPrioridadAlta = parsed.filter(
      (p) => p.prioridad === 'AA' || p.prioridad === 'AAA'
    ).length;
    return { total, padres: 0, subtareas: 0, conCliente, conFecha, conPrioridadAlta, isHierarchical: false };
  }, [text]);

  if (!stats || stats.total === 0) return null;

  return (
    <div className="flex items-center gap-4 text-sm text-muted-foreground bg-muted/50 rounded-md px-3 py-2">
      <span className="font-medium text-foreground">
        {stats.total} {stats.total === 1 ? 'linea detectada' : 'lineas detectadas'}
      </span>
      <Separator orientation="vertical" className="h-4" />
      {stats.isHierarchical && (
        <>
          <span className="text-blue-600">{stats.padres} padres, {stats.subtareas} subtareas</span>
          <Separator orientation="vertical" className="h-4" />
        </>
      )}
      <span>{stats.conCliente} con cliente</span>
      <span>{stats.conFecha} con fecha</span>
      {stats.conPrioridadAlta > 0 && (
        <span className="text-orange-600">{stats.conPrioridadAlta} prioridad alta</span>
      )}
    </div>
  );
}

function CodeReference() {
  const [open, setOpen] = useState(false);

  return (
    <div className="border rounded-md">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 w-full px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        Referencia de codigos semanticos
      </button>
      {open && (
        <div className="px-3 pb-3">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left">
                <th className="pb-1 pr-4 font-medium text-muted-foreground">Codigo</th>
                <th className="pb-1 pr-4 font-medium text-muted-foreground">Descripcion</th>
                <th className="pb-1 font-medium text-muted-foreground">Ejemplo</th>
              </tr>
            </thead>
            <tbody>
              {CODE_REFERENCE.map((item) => (
                <tr key={item.code} className="border-b last:border-0">
                  <td className="py-1 pr-4">
                    <code className="bg-muted px-1 rounded text-xs">{item.code}</code>
                  </td>
                  <td className="py-1 pr-4 text-muted-foreground">{item.description}</td>
                  <td className="py-1 text-muted-foreground">{item.example}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatusIcon({ status }: { status: BulkItemResult['status'] }) {
  switch (status) {
    case 'ok':
      return <CheckCircle className="h-4 w-4 text-green-600" />;
    case 'warning':
      return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
    case 'error':
      return <XCircle className="h-4 w-4 text-red-600" />;
  }
}

function PriorityBadge({ prioridad }: { prioridad: string }) {
  const classes =
    prioridad === 'AAA'
      ? 'bg-red-100 text-red-800'
      : prioridad === 'AA'
        ? 'bg-orange-100 text-orange-800'
        : 'bg-gray-100 text-gray-600';
  return <Badge className={classes}>{prioridad}</Badge>;
}

function PreviewRow({
  result: r,
  isExcluded,
  hasError,
  preview,
  isChild,
  hasChildren,
  isCollapsed,
  isIndeterminate,
  onToggle,
  onToggleCollapse,
}: {
  result: BulkItemResult;
  isExcluded: boolean;
  hasError: boolean;
  preview: BulkItemResult['actividad_preview'];
  isChild: boolean;
  hasChildren: boolean;
  isCollapsed: boolean;
  isIndeterminate: boolean;
  onToggle: () => void;
  onToggleCollapse?: () => void;
}) {
  return (
    <Fragment>
      <tr
        className={cn(
          'border-b',
          hasError && 'bg-red-50/50',
          isExcluded && 'opacity-50',
          isChild && 'bg-muted/20'
        )}
      >
        <td className="p-2">
          <Checkbox
            checked={isIndeterminate ? 'indeterminate' : !isExcluded}
            onCheckedChange={onToggle}
          />
        </td>
        <td className="p-2 text-muted-foreground font-mono text-xs">{r.linea}</td>
        <td className="p-2 max-w-[200px]">
          <span
            className="block truncate text-xs text-muted-foreground"
            title={r.texto_original}
          >
            {r.texto_original}
          </span>
        </td>
        <td className="p-2 font-medium">
          <div className="flex items-center gap-1">
            {hasChildren && onToggleCollapse && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onToggleCollapse(); }}
                className="p-0.5 hover:bg-muted rounded"
              >
                {isCollapsed
                  ? <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                  : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
              </button>
            )}
            {isChild && (
              <span className="text-muted-foreground ml-4 mr-1">└─</span>
            )}
            <span>{preview?.titulo ?? r.texto_original}</span>
            {hasChildren && (
              <Badge variant="outline" className="ml-2 text-xs">
                {r.children?.length} sub
              </Badge>
            )}
          </div>
        </td>
        <td className="p-2">
          <div className="flex flex-wrap gap-1">
            {preview?.cliente_nombre && (
              <Badge className="bg-blue-100 text-blue-800">{preview.cliente_nombre}</Badge>
            )}
            {preview?.contacto_nombre && (
              <Badge className="bg-green-100 text-green-800">{preview.contacto_nombre}</Badge>
            )}
            {preview?.tipo_actividad && (
              <Badge className="bg-orange-100 text-orange-800">{preview.tipo_actividad}</Badge>
            )}
            {preview?.proyecto_nombre && (
              <Badge className="bg-purple-100 text-purple-800">{preview.proyecto_nombre}</Badge>
            )}
            {preview?.ubicacion_nombre && (
              <Badge className="bg-teal-100 text-teal-800">{preview.ubicacion_nombre}</Badge>
            )}
            {preview?.fecha_agendada && (
              <Badge variant="outline">{preview.fecha_agendada}</Badge>
            )}
            {preview?.etiquetas?.map((tag) => (
              <Badge key={tag} className="bg-gray-100 text-gray-800">{tag}</Badge>
            ))}
            {preview?.etiquetas_nuevas?.map((tag) => (
              <Badge key={`new-${tag}`} className="bg-gray-100 text-gray-800 border-dashed border">
                {tag} (nueva)
              </Badge>
            ))}
          </div>
        </td>
        <td className="p-2">
          {preview && <PriorityBadge prioridad={preview.prioridad} />}
        </td>
        <td className="p-2">
          {r.status === 'warning' && r.warnings.length > 0 ? (
            <SimpleTooltip content={r.warnings.join(', ')}>
              <span><StatusIcon status={r.status} /></span>
            </SimpleTooltip>
          ) : (
            <StatusIcon status={r.status} />
          )}
        </td>
      </tr>
      {hasError && r.errores.length > 0 && (
        <tr className="bg-red-50/50 border-b">
          <td colSpan={7} className="px-2 pb-2 pt-0">
            <p className="text-xs text-red-600 ml-10">{r.errores.join('. ')}</p>
          </td>
        </tr>
      )}
    </Fragment>
  );
}

function PreviewTable({
  resultados,
  excludedLines,
  onToggleLine,
  onToggleGroup,
  collapsedGroups,
  onToggleCollapse,
}: {
  resultados: BulkItemResult[];
  excludedLines: Set<number>;
  onToggleLine: (linea: number) => void;
  onToggleGroup: (parentLinea: number) => void;
  collapsedGroups: Set<number>;
  onToggleCollapse: (parentLinea: number) => void;
}) {
  return (
    <div className="border rounded-md overflow-auto max-h-[500px]">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 sticky top-0">
          <tr className="border-b text-left">
            <th className="p-2 w-8" />
            <th className="p-2 w-10">#</th>
            <th className="p-2 w-48">Original</th>
            <th className="p-2">Titulo</th>
            <th className="p-2">Detalles</th>
            <th className="p-2 w-16">Prio.</th>
            <th className="p-2 w-10" />
          </tr>
        </thead>
        <tbody>
          {resultados.map((r) => {
            const isExcluded = excludedLines.has(r.linea);
            const hasError = r.status === 'error';
            const preview = r.actividad_preview;
            const hasChildren = (r.children?.length ?? 0) > 0;
            const isCollapsed = collapsedGroups.has(r.linea);

            // Check if all children are excluded (for indeterminate state)
            const childrenExcluded = r.children?.filter((c) => excludedLines.has(c.linea)).length ?? 0;
            const childrenTotal = r.children?.length ?? 0;
            const isIndeterminate = hasChildren && childrenExcluded > 0 && childrenExcluded < childrenTotal;

            return (
              <Fragment key={r.linea}>
                <PreviewRow
                  result={r}
                  isExcluded={isExcluded}
                  hasError={hasError}
                  preview={preview}
                  isChild={false}
                  hasChildren={hasChildren}
                  isCollapsed={isCollapsed}
                  isIndeterminate={isIndeterminate}
                  onToggle={() => hasChildren ? onToggleGroup(r.linea) : onToggleLine(r.linea)}
                  onToggleCollapse={hasChildren ? () => onToggleCollapse(r.linea) : undefined}
                />
                {hasChildren && !isCollapsed && r.children!.map((child) => {
                  const childExcluded = excludedLines.has(child.linea);
                  const childHasError = child.status === 'error';
                  return (
                    <PreviewRow
                      key={child.linea}
                      result={child}
                      isExcluded={childExcluded}
                      hasError={childHasError}
                      preview={child.actividad_preview}
                      isChild={true}
                      hasChildren={false}
                      isCollapsed={false}
                      isIndeterminate={false}
                      onToggle={() => onToggleLine(child.linea)}
                    />
                  );
                })}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function ImportarActividadesPage() {
  const navigate = useNavigate();
  const catalogIndex = useCatalogIndex();

  // --- State ---
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [rawText, setRawText] = useState('');
  const [activeTab, setActiveTab] = useState<'texto' | 'excel'>('texto');
  const [defaults, setDefaults] = useState<Defaults>({
    area: '',
    prioridad: '',
    cliente_id: '',
  });
  const [previewResults, setPreviewResults] = useState<BulkImportResponse | null>(null);
  const [excludedLines, setExcludedLines] = useState<Set<number>>(new Set());
  const [commitResults, setCommitResults] = useState<BulkImportResponse | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<number>>(new Set());
  const [parsedItems, setParsedItems] = useState<BulkImportItem[]>([]);

  // --- Navigation guard ---
  useEffect(() => {
    if (step !== 2) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [step]);

  // --- Mutations ---
  const previewMutation = useMutation({
    mutationFn: (req: BulkImportRequest) => actividadesService.bulkImport(req),
    onSuccess: (data) => {
      setPreviewResults(data);
      // Auto-exclude error items
      const errorLines = new Set<number>();
      for (const r of data.resultados) {
        if (r.status === 'error') errorLines.add(r.linea);
      }
      setExcludedLines(errorLines);
      setStep(2);
    },
  });

  const commitMutation = useMutation({
    mutationFn: (req: BulkImportRequest) => actividadesService.bulkImport(req),
    onSuccess: (data) => {
      setCommitResults(data);
      setStep(3);
    },
  });

  // --- Handlers ---
  const handleAnalyze = useCallback(() => {
    const catalog = catalogIndex.isLoading ? null : catalogIndex;
    const hierarchical = hasHierarchy(rawText);

    let items: BulkImportItem[];
    if (hierarchical) {
      const groups = parseHierarchy(rawText);
      if (groups.length === 0) return;
      items = convertHierarchyToItems(groups, catalog);
    } else {
      items = convertTextToItems(rawText, catalog);
      if (items.length === 0) return;
    }

    setParsedItems(items);
    previewMutation.mutate({
      mode: 'preview',
      defaults: buildDefaults(defaults),
      items,
    });
  }, [rawText, defaults, previewMutation, catalogIndex]);

  const handleCommit = useCallback(() => {
    if (!previewResults || parsedItems.length === 0) return;

    // Reuse items from preview parse — no re-parsing needed
    const selectedItems = parsedItems
      .filter((item) => !excludedLines.has(item.linea))
      .map((item, i) => ({
        ...item,
        linea: i + 1,
        children: item.children?.filter((c) => !excludedLines.has(c.linea)),
      }));

    if (selectedItems.length === 0) return;

    commitMutation.mutate({
      mode: 'commit',
      defaults: buildDefaults(defaults),
      items: selectedItems,
    });
  }, [previewResults, excludedLines, parsedItems, defaults, commitMutation]);

  const toggleLine = useCallback((linea: number) => {
    setExcludedLines((prev) => {
      const next = new Set(prev);
      if (next.has(linea)) {
        next.delete(linea);
      } else {
        next.add(linea);
      }
      return next;
    });
  }, []);

  // Cascading toggle: select/deselect parent + all children
  const toggleGroup = useCallback((parentLinea: number) => {
    if (!previewResults) return;
    const parent = previewResults.resultados.find((r) => r.linea === parentLinea);
    if (!parent) return;

    setExcludedLines((prev) => {
      const next = new Set(prev);
      const isCurrentlyExcluded = next.has(parentLinea);

      if (isCurrentlyExcluded) {
        // Include parent + all children
        next.delete(parentLinea);
        parent.children?.forEach((c) => next.delete(c.linea));
      } else {
        // Exclude parent + all children
        next.add(parentLinea);
        parent.children?.forEach((c) => next.add(c.linea));
      }
      return next;
    });
  }, [previewResults]);

  const toggleCollapse = useCallback((parentLinea: number) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(parentLinea)) {
        next.delete(parentLinea);
      } else {
        next.add(parentLinea);
      }
      return next;
    });
  }, []);

  const handleReset = useCallback(() => {
    setStep(1);
    setRawText('');
    setPreviewResults(null);
    setExcludedLines(new Set());
    setCommitResults(null);
    setCollapsedGroups(new Set());
    setParsedItems([]);
  }, []);

  const selectedCount = useMemo(() => {
    if (!previewResults) return 0;
    let count = 0;
    for (const r of previewResults.resultados) {
      if (!excludedLines.has(r.linea)) count++;
      for (const c of r.children ?? []) {
        if (!excludedLines.has(c.linea)) count++;
      }
    }
    return count;
  }, [previewResults, excludedLines]);

  // --- Render ---
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Importar Actividades</h1>
          <p className="text-muted-foreground">
            Pega texto natural o usa codigos semanticos — detectamos clientes, horarios y areas automaticamente
          </p>
        </div>
        <StepIndicator step={step} />
      </div>

      {/* Step 1: Data Input */}
      {step === 1 && (
        <StepInput
          rawText={rawText}
          onRawTextChange={setRawText}
          activeTab={activeTab}
          onActiveTabChange={setActiveTab}
          defaults={defaults}
          onDefaultsChange={setDefaults}
          onAnalyze={handleAnalyze}
          isAnalyzing={previewMutation.isPending}
        />
      )}

      {/* Step 2: Preview */}
      {step === 2 && previewResults && (
        <StepPreview
          results={previewResults}
          excludedLines={excludedLines}
          selectedCount={selectedCount}
          onToggleLine={toggleLine}
          onToggleGroup={toggleGroup}
          collapsedGroups={collapsedGroups}
          onToggleCollapse={toggleCollapse}
          onBack={() => setStep(1)}
          onCommit={handleCommit}
          isCommitting={commitMutation.isPending}
          error={commitMutation.error}
        />
      )}

      {/* Step 2: Loading state */}
      {step === 1 && previewMutation.isPending && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Spinner size="lg" />
            <p className="mt-4 text-muted-foreground">Analizando actividades...</p>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Results */}
      {step === 3 && commitResults && (
        <StepResult
          results={commitResults}
          onViewActividades={() => navigate('/actividades')}
          onImportMore={handleReset}
        />
      )}

      {/* Error display */}
      {previewMutation.error && step === 1 && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 text-sm text-red-700">
          Error al analizar: {(previewMutation.error as Error).message ?? 'Error desconocido'}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 1: Input
// ---------------------------------------------------------------------------

function StepInput({
  rawText,
  onRawTextChange,
  activeTab,
  onActiveTabChange,
  defaults,
  onDefaultsChange,
  onAnalyze,
  isAnalyzing,
}: {
  rawText: string;
  onRawTextChange: (text: string) => void;
  activeTab: 'texto' | 'excel';
  onActiveTabChange: (tab: 'texto' | 'excel') => void;
  defaults: Defaults;
  onDefaultsChange: (d: Defaults) => void;
  onAnalyze: () => void;
  isAnalyzing: boolean;
}) {
  const hasContent = rawText.trim().length > 0;

  return (
    <div className="space-y-4">
      <Tabs value={activeTab} onValueChange={(v) => onActiveTabChange(v as 'texto' | 'excel')}>
        <TabsList>
          <TabsTrigger value="texto" className="gap-2">
            <FileText className="h-4 w-4" />
            Texto
          </TabsTrigger>
          <TabsTrigger value="excel" className="gap-2">
            <FileSpreadsheet className="h-4 w-4" />
            Excel/CSV
          </TabsTrigger>
        </TabsList>

        <TabsContent value="texto" className="space-y-4">
          <Textarea
            value={rawText}
            onChange={(e) => onRawTextChange(e.target.value)}
            placeholder={PLACEHOLDER_TEXT}
            rows={12}
            className="font-mono text-sm"
          />

          <LivePreview text={rawText} />

          {/* Defaults bar */}
          <div className="flex items-end gap-4 p-3 bg-muted/30 rounded-md">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Area por defecto</Label>
              <Select
                value={defaults.area}
                onValueChange={(v) => onDefaultsChange({ ...defaults, area: v })}
              >
                <SelectTrigger className="w-[160px] h-9">
                  <SelectValue placeholder="Sin default" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="TRABAJO">Trabajo</SelectItem>
                  <SelectItem value="ADMINISTRATIVO">Administrativo</SelectItem>
                  <SelectItem value="PERSONAL">Personal</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Prioridad por defecto</Label>
              <Select
                value={defaults.prioridad}
                onValueChange={(v) => onDefaultsChange({ ...defaults, prioridad: v })}
              >
                <SelectTrigger className="w-[120px] h-9">
                  <SelectValue placeholder="Sin default" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="A">A (Normal)</SelectItem>
                  <SelectItem value="AA">AA (Alta)</SelectItem>
                  <SelectItem value="AAA">AAA (Urgente)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Cliente por defecto</Label>
              <Input
                value={defaults.cliente_id}
                onChange={(e) => onDefaultsChange({ ...defaults, cliente_id: e.target.value })}
                placeholder="ID o nombre"
                className="w-[160px] h-9"
              />
            </div>
          </div>

          <CodeReference />
        </TabsContent>

        <TabsContent value="excel">
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <Upload className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium text-muted-foreground mb-2">
                Proximamente
              </h3>
              <p className="text-sm text-muted-foreground max-w-md">
                La importacion desde Excel y CSV estara disponible pronto.
                Por ahora, usa la pestana de texto con codigos semanticos.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Action */}
      <div className="flex justify-end">
        <Button
          onClick={onAnalyze}
          disabled={!hasContent || isAnalyzing}
          loading={isAnalyzing}
        >
          Analizar y previsualizar
          <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2: Preview & Validation
// ---------------------------------------------------------------------------

function StepPreview({
  results,
  excludedLines,
  selectedCount,
  onToggleLine,
  onToggleGroup,
  collapsedGroups,
  onToggleCollapse,
  onBack,
  onCommit,
  isCommitting,
  error,
}: {
  results: BulkImportResponse;
  excludedLines: Set<number>;
  selectedCount: number;
  onToggleLine: (linea: number) => void;
  onToggleGroup: (parentLinea: number) => void;
  collapsedGroups: Set<number>;
  onToggleCollapse: (parentLinea: number) => void;
  onBack: () => void;
  onCommit: () => void;
  isCommitting: boolean;
  error: Error | null;
}) {
  const { resumen } = results;

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      <div className="flex items-center gap-3 bg-muted/50 rounded-md px-4 py-3">
        <span className="font-medium">{resumen.total} actividades</span>
        <Separator orientation="vertical" className="h-4" />
        <span className="flex items-center gap-1 text-sm text-green-700">
          <CheckCircle className="h-3.5 w-3.5" />
          {resumen.ok} listas
        </span>
        {resumen.warnings > 0 && (
          <span className="flex items-center gap-1 text-sm text-yellow-700">
            <AlertTriangle className="h-3.5 w-3.5" />
            {resumen.warnings} con advertencias
          </span>
        )}
        {resumen.errores > 0 && (
          <span className="flex items-center gap-1 text-sm text-red-700">
            <XCircle className="h-3.5 w-3.5" />
            {resumen.errores} con error
          </span>
        )}
        <div className="ml-auto text-sm text-muted-foreground">
          {selectedCount} seleccionadas para crear
        </div>
      </div>

      <PreviewTable
        resultados={results.resultados}
        excludedLines={excludedLines}
        onToggleLine={onToggleLine}
        onToggleGroup={onToggleGroup}
        collapsedGroups={collapsedGroups}
        onToggleCollapse={onToggleCollapse}
      />

      {/* Error from commit attempt */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 text-sm text-red-700">
          Error al crear: {error.message ?? 'Error desconocido'}
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Volver
        </Button>
        <Button
          onClick={onCommit}
          disabled={selectedCount === 0 || isCommitting}
          loading={isCommitting}
        >
          Crear {selectedCount} {selectedCount === 1 ? 'actividad' : 'actividades'}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3: Confirmation
// ---------------------------------------------------------------------------

function StepResult({
  results,
  onViewActividades,
  onImportMore,
}: {
  results: BulkImportResponse;
  onViewActividades: () => void;
  onImportMore: () => void;
}) {
  const created = results.resumen.ok;
  const errors = results.resumen.errores;

  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-12 text-center">
        {errors === 0 ? (
          <CheckCircle className="h-16 w-16 text-green-500 mb-4" />
        ) : (
          <AlertTriangle className="h-16 w-16 text-yellow-500 mb-4" />
        )}

        <h2 className="text-xl font-semibold mb-2">
          {created} {created === 1 ? 'actividad creada' : 'actividades creadas'} exitosamente
        </h2>

        {errors > 0 && (
          <p className="text-sm text-red-600 mb-4">
            {errors} {errors === 1 ? 'actividad' : 'actividades'} no se pudieron crear
          </p>
        )}

        <div className="flex gap-3 mt-4">
          <Button variant="outline" onClick={onImportMore}>
            Importar mas
          </Button>
          <Button onClick={onViewActividades}>
            Ver actividades
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
